
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { GlobeAltIcon, ShieldCheckIcon, ChartBarIcon, CursorArrowRaysIcon } from "@heroicons/react/24/solid";
import { useCookieConsent, CookiePreferences } from '@/context/CookieConsentContext';

const CookieSettingsModal: React.FC = () => {
  const { showSettings, closeSettings, preferences, savePreferences } = useCookieConsent();
  const [localPreferences, setLocalPreferences] = useState<CookiePreferences>(preferences);

  useEffect(() => {
    setLocalPreferences(preferences);
  }, [preferences, showSettings]);

  const handleSave = () => {
    savePreferences(localPreferences);
  };

  const handleToggle = (category: keyof CookiePreferences, value: boolean) => {
    if (category === 'essential') return; // Essential cookies cannot be disabled
    
    setLocalPreferences(prev => ({
      ...prev,
      [category]: value,
    }));
  };

  const cookieCategories = [
    {
      key: 'essential' as keyof CookiePreferences,
      title: 'Essential Cookies',
      description: 'Required for basic site functionality, security, and user authentication. These cannot be disabled.',
      icon: ShieldCheckIcon,
      required: true,
      details: [
        'User authentication and session management',
        'Security features and fraud prevention',
        'Basic site functionality and navigation',
        'Shopping cart and checkout processes',
      ],
    },
    {
      key: 'analytics' as keyof CookiePreferences,
      title: 'Analytics Cookies',
      description: 'Help us understand how visitors use our website to improve performance and user experience.',
      icon: ChartBarIcon,
      required: false,
      details: [
        'Page views and user behavior tracking',
        'Performance monitoring and optimization',
        'Error tracking and debugging',
        'Feature usage statistics',
      ],
    },
    {
      key: 'marketing' as keyof CookiePreferences,
      title: 'Marketing Cookies',
      description: 'Used for advertising, personalization, and marketing campaign effectiveness.',
      icon: CursorArrowRaysIcon,
      required: false,
      details: [
        'Personalized content and recommendations',
        'Advertising campaign tracking',
        'Social media integration',
        'Third-party marketing tools',
      ],
    },
  ];

  return (
    <Dialog open={showSettings} onOpenChange={closeSettings}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <GlobeAltIcon className="h-5 w-5" />
            Cookie Preferences
          </DialogTitle>
          <DialogDescription>
            Manage your cookie preferences for AccZen.net. You can enable or disable different categories 
            of cookies based on your privacy preferences.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {cookieCategories.map((category) => {
            const Icon = category.icon;
            const isEnabled = localPreferences[category.key];
            
            return (
              <Card key={category.key} className="relative">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Icon className="h-5 w-5 text-primary" />
                      <div>
                        <CardTitle className="text-base">{category.title}</CardTitle>
                        {category.required && (
                          <span className="text-xs text-muted-foreground font-normal">
                            (Required)
                          </span>
                        )}
                      </div>
                    </div>
                    <Switch
                      checked={isEnabled}
                      onCheckedChange={(value) => handleToggle(category.key, value)}
                      disabled={category.required}
                    />
                  </div>
                  <CardDescription className="text-sm">
                    {category.description}
                  </CardDescription>
                </CardHeader>
                
                <CardContent className="pt-0">
                  <div className="space-y-1">
                    <h4 className="text-sm font-medium text-muted-foreground mb-2">
                      This includes:
                    </h4>
                    <ul className="text-sm text-muted-foreground space-y-1">
                      {category.details.map((detail, index) => (
                        <li key={index} className="flex items-start gap-2">
                          <span className="w-1 h-1 bg-muted-foreground rounded-full mt-2 flex-shrink-0" />
                          {detail}
                        </li>
                      ))}
                    </ul>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t">
          <Button variant="outline" onClick={closeSettings} className="flex-1">
            Cancel
          </Button>
          <Button onClick={handleSave} className="flex-1">
            Save Preferences
          </Button>
        </div>

        <div className="text-xs text-muted-foreground text-center pt-2">
          Your preferences will be saved for one year. You can change them at any time in your account settings.
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CookieSettingsModal;
